import cookies from 'js-cookie'
import {makeAutoObservable, observable, toJS} from 'mobx'
import {type z} from 'zod'
import {AppMode} from 'types/AppMode'
import {cookiesSchemas, type CookieStorageKey} from 'types/CookieStorageKey'
import {type IStorageService} from 'types/IStorageService'

////////////////////////////////////////////////////////////////////////////////

export class CookieStorageService
  implements IStorageService<CookieStorageKey, typeof cookiesSchemas>
{
  constructor() {
    makeAutoObservable(this)
    this.#syncFromDocument()
  }

  /**
   * This map exists so we can easily access and manipulate cookie values in a reactive way.
   *
   * As an example, because we are using this intermediary map the following code becomes
   * reactive.
   * get myCookie() {
   *  return this.#cookieStorageService.get(CookieStorageKey.MY_COOKIE)
   * }
   */
  private values = observable.map<string, unknown>()

  #abortController = new AbortController()
  #domain =
    import.meta.env.MODE === AppMode.PROD
      ? import.meta.env.VITE_COOKIE_DOMAIN
      : // Used for testing
        (window.COOKIE_DOMAIN ?? import.meta.env.VITE_COOKIE_DOMAIN)

  #defaultCookieAttributes: Cookies.CookieAttributes = {
    expires: 365,
    domain: this.#domain,
    sameSite: 'lax',
  }

  #dangerouslyGet<T = any>(key: string): T | undefined {
    const rawValue = cookies.get(key)
    if (rawValue === undefined) {
      return rawValue
    }

    try {
      return JSON.parse(rawValue) as T
    } catch (error) {
      return undefined
    }
  }

  /**
   * Returns the value of the cookie with the given key, or undefined if it doesn't exist.
   */
  get<TKey extends CookieStorageKey>(key: TKey) {
    const rawValue = toJS(this.values.get(key))
    if (rawValue === undefined) {
      return undefined
    }
    const schema = cookiesSchemas[key]
    const {data} = schema.safeParse(rawValue)

    return data as z.infer<(typeof cookiesSchemas)[TKey]>
  }

  /**
   * Returns the value of the cookie with the given key, or the default value if it doesn't exist.
   */
  getOrUseDefault<TKey extends CookieStorageKey>(key: TKey) {
    const rawValue = toJS(this.values.get(key))
    const schema = cookiesSchemas[key]
    const {data} = schema.safeParse(rawValue)
    return data as z.infer<(typeof cookiesSchemas)[TKey]>
  }

  set<T extends CookieStorageKey>(
    key: T,
    value: z.infer<(typeof cookiesSchemas)[T]>,
    options?: Cookies.CookieAttributes
  ) {
    const opts = {...this.#defaultCookieAttributes, ...options}
    cookies.set(key, JSON.stringify(value), opts)
    this.values.set(key, value)
  }

  has(key: CookieStorageKey) {
    return !!cookies.get(key)
  }

  remove(key: CookieStorageKey | string, options?: Cookies.CookieAttributes) {
    const opts = {...this.#defaultCookieAttributes, ...options}
    cookies.remove(key, opts)
    this.values.delete(key)
  }

  list(): Record<string, string> {
    return Object.fromEntries(
      document.cookie.split('; ').map(c => c.split('='))
    )
  }

  #syncFromDocument() {
    const allKeys = Object.keys(cookies.get())

    allKeys.forEach(key => {
      this.values.set(key, this.#dangerouslyGet(key))
    })
  }

  dispose() {
    this.#abortController.abort()
  }
}
